import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CarouselItem, Spacer, Typography } from "@aviary";
import { AuthFooter } from "@shared/components/AuthFooter/AuthFooter";
import { AuthHeader } from "@shared/components/AuthHeader/AuthHeader";
import { AuthHeaderWrapper } from "@shared/components/AuthHeaderWrapper/AuthHeaderWrapper";
import { UnauthBrowseLabTestsEntryPoint } from "@shared/hooks/useAnalyticsLogger/UnauthAnalyticsEntryPoint";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FooterSignIn } from "@unauthenticated/shared/components/FooterSignIn/FooterSignIn";
import { FormWrapper } from "@unauthenticated/shared/components/FormWrapper/FormWrapper";
import { SignUpCarousel } from "@unauthenticated/shared/components/SignUpCarousel/SignUpCarousel";
import { SignUpTitle } from "@unauthenticated/shared/components/SignUpTitle/SignUpTitle";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { MarketplaceCtaBanner } from "@unauthenticated/signupSignin/components/MarketplaceCtaBanner/MarketplaceCtaBanner";
import { Page } from "@unauthenticated/signupSignin/components/Page/Page";
import { PageContent } from "@unauthenticated/signupSignin/components/PageContent/PageContent";
import { PatientCheckEmail } from "@unauthenticated/signupSignin/components/PatientCheckEmail/PatientCheckEmail";
import { l } from "@unauthenticated/signupSignin/locales/i18n";
import { EmailSentPage } from "@unauthenticated/signupSignin/scenes/EmailSentPage/EmailSentPage";
import { ImAPractitionerButton } from "@unauthenticated/signupSignin/scenes/PatientSignUpPage/ImAPractitionerButton/ImAPractitionerButton";

import * as styles from "./PatientSignUpPage.styles";

interface Props {
  pageVersion: "prac_by_default_with_sso" | "prac_by_default";
}

const PatientSignUpPage = ({ pageVersion }: Props) => {
  const [email, setEmail] = useState("");
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();

  if (email) {
    return <EmailSentPage email={email} />;
  }

  const renderCarouselOrFooter = (isWithinViewport: boolean) => {
    if (isWithinViewport) {
      return (
        <SignUpCarousel>
          <CarouselItem>
            <Typography type="footnote">{t(l.practitionerSignUp.PatientUSPOne)}</Typography>
          </CarouselItem>
          <CarouselItem>
            <Typography type="footnote">{t(l.practitionerSignUp.PatientUSPTwo)}</Typography>
          </CarouselItem>
          <CarouselItem>
            <Typography type="footnote">{t(l.practitionerSignUp.PatientUSPThree)}</Typography>
          </CarouselItem>
          <CarouselItem>
            <Typography type="footnote">{t(l.practitionerSignUp.PatientUSPFour)}</Typography>
          </CarouselItem>
          <CarouselItem>
            <Typography type="footnote">{t(l.practitionerSignUp.PatientUSPFive)}</Typography>
          </CarouselItem>
        </SignUpCarousel>
      );
    }

    return (
      <AuthFooter>
        <FooterSignIn />
      </AuthFooter>
    );
  };

  return (
    <Page>
      <PageContent>
        <AuthHeaderWrapper>
          <AuthHeader route={authRoutes.login} />
        </AuthHeaderWrapper>
        <FormWrapper>
          <div css={styles.textAlign}>
            <ImAPractitionerButton />
            <SignUpTitle user={t(l.practitionerSignUp.Patient)} />
            <Typography css={styles.subTitle}>
              <Trans i18nKey={l.practitionerSignUp.LetsGetStarted}>
                Let's get you started! All accounts are 100%{" "}
                <strong css={styles.freeText}>free</strong>
              </Trans>
            </Typography>
            <Spacer height="doubleHalf" />
            <PatientCheckEmail
              onEmailSent={setEmail}
              pageVersion={pageVersion}
              signUpPage="patient_check_invitation_page"
            />
          </div>
          {renderCarouselOrFooter(tablet.greaterThan)}
        </FormWrapper>
      </PageContent>
      <MarketplaceCtaBanner entryPoint={UnauthBrowseLabTestsEntryPoint.PATIENT_SIGN_UP} />
      {renderCarouselOrFooter(!tablet.greaterThan)}
    </Page>
  );
};

export { PatientSignUpPage };
